import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection } from '@/hooks/firestore';
import { useAuth } from '@/stores/auth';
import { VideoCard } from '@/components/services/VideoCard';
import { buildFeedSlides, type ServiceDoc } from '@makhe/shared';

export default function FeedPage({ isVisible = true }: { isVisible?: boolean }) {
  const q = useMemo(
    () =>
      query(
        collection(db, 'services'),
        where('status', '==', 'active'),
        orderBy('stats.averageRating', 'desc'),
        orderBy('createdAt', 'desc'),
        limit(20),
      ),
    [],
  );
  const { data: services, loading } = useCollection<ServiceDoc>(q);
  const feedServices = useMemo(
    () => services.filter((s) => buildFeedSlides(s.media).length > 0),
    [services],
  );
  const [activeIdx, setActiveIdx] = useState(0);
  // Mute is global (one tap toggles the whole feed) and survives scrolling
  // between cards. Default to sound ON; if the browser blocks unmuted autoplay
  // (no user gesture yet) a card reports back and we fall back to muted so the
  // video still plays — the user can then tap to unmute.
  const [muted, setMuted] = useState(false);
  const onAutoplayBlocked = useCallback(() => setMuted(true), []);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  // Pick the active card with an IntersectionObserver rooted on the scroll
  // container. This re-runs once the services load (deps include the count), so
  // it can't miss the ref the way a one-shot scroll listener did.
  useEffect(() => {
    const root = containerRef.current;
    if (!root || feedServices.length === 0 || !isVisible) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) {
            const idx = Number((e.target as HTMLElement).dataset.idx);
            if (!Number.isNaN(idx)) setActiveIdx(idx);
          }
        }
      },
      { root, threshold: [0.6] },
    );
    itemRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [feedServices.length, isVisible]);

  // Fill the content area between header and nav exactly — the flex layout in
  // AppShell makes this deterministic, no viewport math needed.
  const frame = 'h-full';

  if (loading) {
    return (
      <div className={`grid place-items-center bg-makhe-night ${frame}`}>
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white" />
      </div>
    );
  }

  if (feedServices.length === 0) {
    return <EmptyFeed />;
  }

  return (
    <div
      ref={containerRef}
      className={`snap-y snap-mandatory overflow-y-scroll bg-makhe-night ${frame}`}
    >
      {feedServices.map((s, i) => {
        // Window the feed: only mount the heavy VideoCard for the active card and
        // its immediate neighbours. Off-screen cards render a cheap poster
        // placeholder so the snap-scroll height/positions stay correct without
        // buffering video for cards the user hasn't reached (mobile-data cost).
        const near = Math.abs(i - activeIdx) <= 1;
        return (
          <div
            key={s.id}
            data-idx={i}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            className={`w-full snap-start ${frame}`}
          >
            {near ? (
              <VideoCard
                service={s}
                active={i === activeIdx && isVisible}
                feedVisible={isVisible}
                muted={muted}
                onToggleMute={() => setMuted((m) => !m)}
                onAutoplayBlocked={onAutoplayBlocked}
              />
            ) : (
              <FeedPosterPlaceholder service={s} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function FeedPosterPlaceholder({ service }: { service: ServiceDoc }) {
  const poster = service.media.coverImageURL || service.media.videoThumbnailURL;
  return (
    <div className="h-full w-full bg-makhe-night">
      {poster && (
        <img
          src={poster}
          alt=""
          aria-hidden
          loading="lazy"
          className="h-full w-full object-cover opacity-70"
        />
      )}
    </div>
  );
}

function EmptyFeed() {
  const { isSeller, isVerifiedStudent } = useAuth();
  const canCreate = isSeller();
  return (
    <div className="grid h-full place-items-center bg-makhe-night px-8 text-center text-white">
      <div className="max-w-sm">
        <p className="font-display text-3xl leading-tight">No services here yet</p>
        <p className="mt-3 text-sm text-white/70">
          Creators are still setting up. Check back soon — or be the first to put your
          work in front of students.
        </p>
        <div className="mt-6">
          {canCreate ? (
            <Link
              to="/seller/services/new"
              className="inline-flex rounded-full bg-white px-6 py-3 text-[11px] uppercase tracking-widest text-makhe-night"
            >
              Add your first service
            </Link>
          ) : (
            <Link
              to={isVerifiedStudent() ? '/seller/onboarding' : '/profile/verify-student'}
              className="inline-flex rounded-full bg-white px-6 py-3 text-[11px] uppercase tracking-widest text-makhe-night"
            >
              Become a creator
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
